// src/components/GridView/types.ts
import type { ConversationDetail } from '@/types/conversation';

// ==================== Constants ====================

export const GRID_PAGE_SIZE = 50;
export const GRID_TITLE_COLUMN_WIDTH = 350;
export const GRID_DEFAULT_COLUMN_WIDTH = 180;
export const GRID_LICENSE_KEY = 'non-commercial-and-evaluation';
export const GRID_HEADER_HEIGHT = 25;

export const FIELD_TYPES = {
  TEXT: 1,
  INTEGER: 2,
  DECIMAL: 3,
  INTEGER_ALT: 4,
  MULTILINE_TEXT: 5,
  DATE: 6,
  MULTI_SELECT: 7,
  COMBO_ALT: 8,
  COMBO: 9,
  IMAGE: 10,
  HYPERLINK: 11,
} as const;

/** Field types that support inline editing in the grid */
export const GRID_EDITABLE_FIELD_TYPES = new Set([1, 2, 3, 4, 5, 6, 7, 8, 9, 11]);

// ==================== Component Props ====================

export interface GridViewProps {
  dashboardId: number;
  configValueId?: number;
  projectId?: string;
  filters?: string;
  searchText?: string;
  layersToHide?: string;
  isActive?: boolean;
  dashboardName?: string;
  /** Column titles to exclude from the grid (exact match against FieldName) */
  excludeColumnTitles?: string[];
  /** PersonId — when set, fetches via GetViewMeDetailInfo filtered to this person (ME View) */
  me?: number;
  onOpenInfoWindow?: (conversation: ConversationDetail) => void;
  onUpdateConversation?: (conversationId: number, updates: Partial<ConversationDetail>) => void;
  extraFilterParams?: Record<string, any>;
}

export interface GridViewRef {
  exportToExcel: () => void;
}

// ==================== API Request ====================

export interface GridViewRequestParams {
  CustomFields: any[];
  PageNumber: number;
  PageSize: number;
  DashboardId: number;
  Project: string;
  Filters: string;
  SearchText: string;
  LayersToHide: string;
  DashboardViewTypeId: number;
  IsFilterPersonal: boolean;
  ConfigValueId?: number;
  ProjectId?: string | number;
  [key: string]: any;
}

// ==================== API Response ====================

export interface GridViewResponse {
  Conversations: ConversationDetail[];
  TotalItems?: number;
}

// ==================== Column Definition ====================

export interface GridColumn {
  /** Unique key used as the data field in HotTable rows */
  key: string;
  title: string;
  layerId?: number;
  layerName?: string;
  layerColor?: string;
  /** Primary customFieldId (first encountered for this layer+name pair) */
  customFieldId?: number;
  /** All customFieldIds sharing this layer+name — used when saving to handle
   *  duplicate fields (same name, different IDs) merged into a single column. */
  customFieldIds?: number[];
  fieldTypeId?: number;
  width?: number;
  /** For type-7 person multi-selects: the API ServiceUrl from the field definition */
  serviceUrl?: string;
  /** Label field name returned by ServiceUrl (e.g. "Name") */
  textField?: string;
  /** Value field name returned by ServiceUrl (e.g. "PersonID") */
  valueField?: string;
}

// ==================== Row ====================

export interface GridRow {
  conversationId: number;
  title: string;
  icon: string;
  isRedFlag: boolean;
  isImportant: boolean | null;
  createdAt: string;
  configValueId: number;
  [fieldKey: string]: any;
}

// ==================== Hidden Columns ====================

export interface HiddenColumnsRequest {
  dashboardId?: number;
  projectId?: number;
  configValueId?: number;
}

export interface UpdateHiddenColumnsRequest {
  DashboardId: number;
  Project: string | number;
  ConfigValueId?: number;
  Columns: string; // CSV: "37_12,53_12"
}

// ==================== Save Field ====================

export interface SaveFieldRequest {
  CustomFieldValueId?: number;
  CustomFieldId: number;
  /** Backend key for the conversation — the API ignores ConversationId and reads CoreTaskId */
  CoreTaskId: number;
  ConversationId?: null;
  Value: any;
  SelectedValuesText?: string;
  LayerId?: number;
  SocketConnectionId?: string;
  [key: string]: any;
}

export interface SaveMultipleFieldRequest {
  CustomFieldValueId?: number;
  CustomFieldId: number;
  CoreTaskId: number;
  ConversationId?: number | null;
  Value: any;
  SelectedValuesText?: string;
  LayerId?: number;
  [key: string]: any;
}

// ==================== Update Title ====================

export interface UpdateConversationTitleRequest {
  ConversationId: number;
  Title: string;
  SocketConnectionId?: string;
}

// ==================== Editing Cell ====================

/** Viewport-relative position of a grid cell, used to place the cell editor. */
export interface GridCellRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface EditingCellState {
  visualRow: number;
  visualCol: number;
  column: GridColumn;
  conversation: ConversationDetail;
  customField: any;
  currentValue: any;
  /** Measured when editing starts; re-measured on scroll/resize via getCellRect. */
  cellRect: GridCellRect;
}

// ==================== Export ====================

export interface ExportSpreadSheetRequest extends GridViewRequestParams {
  ExportFormat?: string;
}
